// src/components/StorePage.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../AuthStyles.css';
import { LABELS, BRAND_COLOR, PM_GROUPS, CARD_TOUCH_MUTEX } from '../paymentDefs';
import { PAYMENT_LABELS, PAYMENT_COLORS, CARD_TAP_PAIRS } from '../constants/payments';
import { CATEGORY_OPTIONS as CATEGORIES } from '../constants/categories';

const API_BASE = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001').replace(/\/+$/, '');

// ラベル/色は constants/payments を優先し、無ければ従来値にフォールバック
const getLabel = (id) => (PAYMENT_LABELS?.[id] ?? LABELS?.[id] ?? id);
const getColor = (id) => (PAYMENT_COLORS?.[id] ?? BRAND_COLOR?.[id] ?? '#555');
const activePaymentMethods = (methods) =>
  (Array.isArray(methods) ? methods : []).filter((id) => id !== 'line_pay');

// カード配列（PM_GROUPS.card）を「タッチあり/なし」に展開
const useExpandedCardIds = (baseCardIds) => {
  return React.useMemo(() => {
    const src = Array.isArray(baseCardIds) ? baseCardIds : [];
    return src.flatMap((id) => {
      const pair = CARD_TAP_PAIRS?.[id];
      return pair ? [pair.tap, pair.noTap] : [id];
    });
  }, [baseCardIds]);
};


const resolveAuthEmail = (search) => {
  try {
    const qs = new URLSearchParams(search || '');
    const q = qs.get('email');
    return q || localStorage.getItem('authEmail') || '';
  } catch {
    return localStorage.getItem('authEmail') || '';
  }
};

const ToggleBadge = ({ id, selected, onToggle }) => {
  const label = getLabel(id);
  const color = getColor(id);
  return (
    <button
      type="button"
      onClick={() => onToggle(id)}
      title={label}
      style={{
        padding: '6px 10px',
        borderRadius: 20,
        border: selected ? `2px solid ${color}` : `1px solid ${color}`,
        background: selected ? color : '#fff',
        color: selected ? '#fff' : color,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        cursor: 'pointer',
        fontSize: 12,
        fontWeight: 600,
        margin: '6px 6px 0 0',
        outline: 'none'
      }}
    >
      {label}
    </button>
  );
};

export default function StorePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const email = useMemo(() => resolveAuthEmail(location.search), [location.search]);

  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');

  const [selectedShop, setSelectedShop] = useState(null);
  const [ownerName, setOwnerName] = useState('');
  const [shopName, setShopName] = useState('');
  const [category, setCategory] = useState('');
  const [selectedPayments, setSelectedPayments] = useState(new Set());
  const [officialWebsite, setOfficialWebsite] = useState('');
  const [officialInstagram, setOfficialInstagram] = useState('');
  const [officialFacebook, setOfficialFacebook] = useState('');
  const [officialX, setOfficialX] = useState('');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authPassword, setAuthPassword] = useState('');
  const [pendingAction, setPendingAction] = useState(null);
  const [modalError, setModalError] = useState(''); // モーダル内のエラーメッセージ用

  // URLの ?shop= または ?shopId= を拾う
  const urlShopId = useMemo(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      return (sp.get('shop') || sp.get('shopId') || '').trim();
    } catch {
      return '';
    }
  }, []);

  // 現在選択中の店舗（shops と selectedShop / urlShopId から決定）
  const currentShop = useMemo(() => {
    const list = Array.isArray(shops) ? shops : [];
    const sid = (selectedShop?._id || urlShopId || '').trim();
    return list.find(s => s && s._id === sid) || null;
  }, [shops, selectedShop, urlShopId]);

  // 初回ロード時：URLがあればそれを優先して選択
  useEffect(() => {
    if (!loading && urlShopId && Array.isArray(shops) && shops.length > 0) {
      const s = shops.find(x => x._id === urlShopId);
      if (s && (!selectedShop || selectedShop._id !== s._id)) {
        onSelectShop(s);
      }
    }
  }, [loading, shops, urlShopId]); // onSelectShop は既存関数

  const expandedCardIds = useExpandedCardIds(PM_GROUPS?.card);
  const GROUPS = useMemo(() => ([
    { key: 'qr',     title: 'QR/バーコード', ids: PM_GROUPS?.qr     || [] },
    { key: 'emoney', title: '電子マネー',   ids: PM_GROUPS?.emoney || [] },
    { key: 'card',   title: '国際ブランド', ids: expandedCardIds    || [] },
    { key: 'wallet', title: 'ウォレット',   ids: PM_GROUPS?.wallet || [] },
    { key: 'local', title: '地域通貨',   ids: PM_GROUPS?.local || [] },
  ]), [expandedCardIds]);

  // ▼ モバイル判定
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 860px)');
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // ▼ 初回にモバイルの「店舗を選択」位置へ自動スクロール（見切れ対策）
  const mobileSelectRef = useRef(null);
  useEffect(() => {
    if (isMobile && mobileSelectRef.current) {
      // 画面上部に確実に見える位置まで
      mobileSelectRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [isMobile, loading]);

  // 初期取得
  useEffect(() => {
    if (!email) {
      navigate('/login');
      return;
    }
    let aborted = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/shops/by-email/${encodeURIComponent(email)}`);
        const text = await res.text();
        let data = null; try { data = JSON.parse(text); } catch {}
        if (!res.ok) throw new Error((data && data.error) || `HTTP ${res.status}`);

        const list = Array.isArray(data?.shops) ? data.shops : Array.isArray(data) ? data : [];
        if (aborted) return;

        setShops(list);
        const first = list[0] || null;
        setSelectedShop(first);
        if (first) {
          setOwnerName(first.ownerName || '');
          setShopName(first.shopName || '');
          setCategory(first.category || '');
          setSelectedPayments(new Set(activePaymentMethods(first.paymentMethods)));
          setOfficialWebsite(first.officialWebsite || '');
          setOfficialInstagram(first.officialInstagram || '');
          setOfficialFacebook(first.officialFacebook || '');
          setOfficialX(first.officialX || '');
        }
      } catch (e) {
        if (aborted) return;
        console.error('fetch shops error', e);
        setMsg('店舗一覧の取得に失敗しました');
        setTimeout(()=>setMsg(''), 2500);
      } finally {
        if (!aborted) setLoading(false);
      }
    })();
    return () => { aborted = true; };
  }, [email, navigate]);

  const onSelectShop = (s) => {
    setSelectedShop(s);
    setOwnerName(s.ownerName || '');
    setShopName(s.shopName || '');
    setCategory(s.category || '');
    setSelectedPayments(new Set(activePaymentMethods(s.paymentMethods)));
    setOfficialWebsite(s.officialWebsite || '');
    setOfficialInstagram(s.officialInstagram || '');
    setOfficialFacebook(s.officialFacebook || '');
    setOfficialX(s.officialX || '');
  };

  const onDeleteShop = (s) => {
    const ok = window.confirm(`「${s.shopName}」を削除します。よろしいですか？`);
    if (!ok) return;
    
    setSelectedShop(s);
    setPendingAction('delete');
    setShowAuthModal(true);
  };

  const executeDelete = async () => {
    if (!selectedShop || !authPassword) return;
    
    try {
      const auth = btoa(`${email}:${authPassword}`);
      
      const res = await fetch(`${API_BASE}/shops/${selectedShop._id}`, { 
        method: 'DELETE',
        headers: {
          'Authorization': `Basic ${auth}`
        }
      });
      const text = await res.text();
      let data = null; try { data = JSON.parse(text); } catch {}
      if (!res.ok) throw new Error((data && data.error) || `HTTP ${res.status}`);

      setShops(prev => prev.filter(x => x._id !== selectedShop._id));
      setSelectedShop(null);
      setMsg('削除しました');
      setTimeout(()=>setMsg(''), 1600);
      
      setShowAuthModal(false);
      setAuthPassword('');
      setPendingAction(null);
    } catch (e) {
      console.error(e);
      setMsg('削除に失敗しました');
      setTimeout(()=>setMsg(''), 2500);
    }
  };

  // 同一ブランド内で「（タッチあり）or（タッチなし）or（旧：素のブランドID）」のいずれか一つだけにする
  const togglePayment = (id) => {
    // クリックされたIDがどのブランドのものかを判定（*_touch / *_no_touch のどちらでもブランド名を返す）
    const findBrandById = (pid) => {
      for (const [brand, pair] of Object.entries(CARD_TAP_PAIRS || {})) {
        if (pid === brand || pid === pair?.tap || pid === pair?.noTap) return brand;
      }
      return null;
    };

    // あるブランドに属する「相互排他ファミリー（素のID／touch／no_touch）」を列挙
    const buildFamilySet = (brand) => {
      const family = [brand];
      const pair = CARD_TAP_PAIRS?.[brand];
      if (pair?.tap) family.push(pair.tap);
      if (pair?.noTap) family.push(pair.noTap);
      return family;
    };

    setSelectedPayments((prev) => {
      const next = new Set(prev);
      const already = next.has(id);

      if (already) {
        // 同じボタンを再度押したら外す
        next.delete(id);
        return next;
      }

      // 追加する前に、同一ブランドの他候補（素のID/touch/no_touch）をすべて外す
      const brand = findBrandById(id);
      if (brand) {
        for (const k of buildFamilySet(brand)) next.delete(k);
      }

      // 今回のIDを追加
      next.add(id);
      return next;
    });
  };



    const onSaveAll = () => {
      if (!selectedShop) return;
      setModalError(''); // モーダルを開く際にエラーをクリア
      setPendingAction('save');
      setShowAuthModal(true);
    };

    const executeSave = async () => {
      if (!selectedShop || !authPassword) return;
      
      try {
        const payload = {
          ownerName: ownerName.trim(),
          shopName: shopName.trim(),
          category: category || 'その他',
          paymentMethods: activePaymentMethods(Array.from(selectedPayments)),
          officialWebsite: officialWebsite.trim(),
          officialInstagram: officialInstagram.trim(),
          officialFacebook: officialFacebook.trim(),
          officialX: officialX.trim(),
        };
        
        const auth = btoa(`${email}:${authPassword}`);
        
        const res = await fetch(`${API_BASE}/shops/${selectedShop._id}`, {
          method: 'PUT',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Basic ${auth}`
          },
          body: JSON.stringify(payload),
        });
        const text = await res.text();
        let data = null; try { data = JSON.parse(text); } catch {}
        if (!res.ok) throw new Error((data && data.error) || `HTTP ${res.status}`);

        setShops(prev => prev.map(s => s._id === selectedShop._id ? { ...s, ...payload } : s));
        setSelectedShop(prev => prev ? { ...prev, ...payload } : prev);
        setMsg('変更を保存しました');
        setTimeout(()=>setMsg(''), 1800);
        
      setShowAuthModal(false);
      setAuthPassword('');
      setPendingAction(null);
      setModalError('');
    } catch (e) {
      console.error(e);
      const errorMsg = e?.message || '';
      // URLバリデーションエラーの場合はモーダル内にエラーを表示
      if (errorMsg.includes('URLが無効') || errorMsg.includes('無効です') || errorMsg.includes('アクセスできません')) {
        setModalError('URLが無効です。正しいURLを入力してください。');
      } else {
        setModalError('保存に失敗しました');
      }
      // モーダルは閉じない（エラーを確認してもらうため）
      // パスワードはクリアしない（再試行しやすくするため）
    }
    };

    // 支払い方法の変更（Billing Portal）
  // 支払い方法の変更（Billing Portal）
  const onClickChangePayment = async () => {
    try {
      // 利用可能なIDを最優先順で拾う
      const sid = (selectedShop?._id || currentShop?._id || urlShopId || '').trim();
      const cid = (selectedShop?.billing?.customerId || currentShop?.billing?.customerId || '').trim();

      if (!sid && !cid) {
        setMsg('店舗が選択されていません');
        setTimeout(() => setMsg(''), 1500);
        return;
      }

      // あるものだけ送る（両方あれば両方）
      const payload = {};
      if (sid) payload.shopId = sid;
      if (cid) payload.customerId = cid;

      const r = await fetch(`${API_BASE}/billing/portal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const raw = await r.text();
      let data = null;
      try { data = JSON.parse(raw); } catch {}

      if (!r.ok || !data?.url) {
        throw new Error((data && data.error) || `HTTP ${r.status}`);
      }

      window.location.href = data.url;
    } catch (e) {
      console.error(e);
      setMsg('ポータルの起動に失敗しました');
      setTimeout(() => setMsg(''), 1800);
    }
  };


  

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:64px_64px]" />
        
        <div className="bg-card/80 backdrop-blur-md border border-border/50 rounded-3xl p-8 shadow-2xl ring-1 ring-white/10 relative z-10">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl mb-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            </div>
            <p className="text-foreground font-medium">読み込み中...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-8 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:64px_64px]" />
      
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="bg-card/80 backdrop-blur-md border border-border/50 rounded-3xl p-8 shadow-2xl ring-1 ring-white/10">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h1 className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent text-3xl font-bold">
              店舗管理
            </h1>
            <p className="text-muted-foreground mt-2">店舗情報の編集と決済方法の管理</p>
          </div>
          <div 
            className="grid gap-6"
            style={{
              gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr',
            }}
          >
            {/* 左：店舗一覧（スマホは非表示） */}
            {!isMobile && (
              <div>
                <h2 className="text-xl font-semibold text-foreground mb-4">登録店舗</h2>
                <div className="max-h-520 overflow-auto pr-2">
              {shops.length === 0 && <div className="text-muted-foreground">まだ店舗がありません。</div>}
              {shops.map((s) => (
                <div
                  key={s._id}
                  onClick={()=>onSelectShop(s)}
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border transition-colors mb-2 ${
                    selectedShop?._id === s._id 
                      ? 'bg-purple-600/20 border-purple-600/50' 
                      : 'bg-background border-border hover:bg-accent'
                  }`}
                >
                  <div className="flex-1">
                    <div className="font-semibold text-foreground">{s.shopName}</div>
                    <div className="text-sm text-muted-foreground">
                      オーナー: {s.ownerName} / 種類: {s.category || 'その他'}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      決済: {activePaymentMethods(s.paymentMethods).length
                        ? activePaymentMethods(s.paymentMethods).map(id=>LABELS[id]||id).join('・')
                        : '未登録'}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={(e)=>{e.stopPropagation(); onDeleteShop(s);}}
                    className="px-4 py-2 bg-white hover:bg-red-50 text-red-600 hover:text-red-700 text-sm font-medium rounded-lg border border-red-600 hover:border-red-700 transition-all shadow-sm hover:shadow-md"
                  >
                    削除
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 右：編集（スマホ時はここだけ） */}
        <div>
          {/* モバイル：上部に店舗プルダウン（見切れ対策で ref を付与） */}
          {isMobile && shops.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <label className="block text-sm font-medium text-black mb-2" style={{ textAlign: 'left', marginBottom: 6 }}>
                店舗を選択
              </label>
              <select
                ref={mobileSelectRef}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-black focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-colors"
                style={{ scrollMarginTop: 12 }}
                value={selectedShop?._id || ''}
                onChange={(e) => {
                     const s = shops.find(x => x._id === e.target.value);
                     if (s) {
                       onSelectShop(s);
                       // URL に ?shopId= を反映（リロードなし・履歴置換）
                       const sp = new URLSearchParams(window.location.search);
                       sp.set('shopId', s._id);
                       window.history.replaceState(null, '', `${window.location.pathname}?${sp.toString()}`);
                     }
                }}
              >
                <option value="">（選択してください）</option>
                {shops.map(s => (
                  <option key={s._id} value={s._id}>
                    {s.shopName || '(無題の店舗)'}
                  </option>
                ))}
              </select>
            </div>
          )}

          <h2 className="text-xl font-semibold text-black mb-4" style={{ marginBottom: 10 }}>店舗情報の編集</h2>

          {selectedShop ? (
            <>
              <div className="auth-row">
                <label className="block text-sm font-medium text-black mb-2">店舗名</label>
                <input 
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-colors" 
                  value={shopName} 
                  onChange={(e)=>setShopName(e.target.value)} 
                  placeholder="例）山田珈琲店" 
                />
              </div>
              <div className="auth-row">
                <label className="block text-sm font-medium text-black mb-2">オーナー名</label>
                <input 
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-colors" 
                  value={ownerName} 
                  onChange={(e)=>setOwnerName(e.target.value)} 
                  placeholder="例）山田 太郎" 
                />
              </div>
              <div className="auth-row">
                <label className="block text-sm font-medium text-black mb-2">カテゴリ</label>
                <select 
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-black focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-colors" 
                  value={category} 
                  onChange={(e)=>setCategory(e.target.value)}
                >
                  <option value="">店舗の種類</option>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <div className="auth-row" style={{ alignItems: 'flex-start' }}>
                <label className="block text-sm font-medium text-black mb-2" style={{ paddingTop: 10 }}>決済方法(選択・保存してください)</label>
                <div>
                  {GROUPS.map(({ key, title, ids }) => (
                    <div key={key} style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 12, opacity: .9, margin: '8px 0 4px' }}>{title}</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                        {ids.map((id) => (
                          <ToggleBadge
                            key={id}
                            id={id}
                            selected={selectedPayments.has(id)}
                            onToggle={togglePayment}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* SNS・公式サイト（任意項目） */}
              <div className="auth-row">
                <label className="block text-sm font-medium text-black mb-2">
                  公式ホームページ <span className="text-gray-500 text-xs font-normal">(任意)</span>
                </label>
                <input 
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-colors" 
                  type="url"
                  value={officialWebsite} 
                  onChange={(e)=>setOfficialWebsite(e.target.value)} 
                  placeholder="例）https://example.com" 
                />
              </div>
              <div className="auth-row">
                <label className="block text-sm font-medium text-black mb-2">
                  公式Instagram <span className="text-gray-500 text-xs font-normal">(任意)</span>
                </label>
                <input 
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-colors" 
                  type="url"
                  value={officialInstagram} 
                  onChange={(e)=>setOfficialInstagram(e.target.value)} 
                  placeholder="例）https://instagram.com/yourshop" 
                />
              </div>
              <div className="auth-row">
                <label className="block text-sm font-medium text-black mb-2">
                  公式Facebook <span className="text-gray-500 text-xs font-normal">(任意)</span>
                </label>
                <input 
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-colors" 
                  type="url"
                  value={officialFacebook} 
                  onChange={(e)=>setOfficialFacebook(e.target.value)} 
                  placeholder="例）https://facebook.com/yourshop" 
                />
              </div>
              <div className="auth-row">
                <label className="block text-sm font-medium text-black mb-2">
                  公式X（Twitter） <span className="text-gray-500 text-xs font-normal">(任意)</span>
                </label>
                <input 
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-colors" 
                  type="url"
                  value={officialX} 
                  onChange={(e)=>setOfficialX(e.target.value)} 
                  placeholder="例）https://x.com/yourshop" 
                />
              </div>

              <div className="auth-actions" style={{ marginTop: 16 }}>
                <div />
                <div className="auth-actions-right" style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  {isMobile && (
                    <button 
                      className="px-4 py-2 bg-white hover:bg-red-50 text-red-600 hover:text-red-700 text-sm font-medium rounded-lg border border-red-600 hover:border-red-700 transition-all shadow-sm hover:shadow-md"
                      onClick={() => onDeleteShop(selectedShop)}
                    >
                      削除
                    </button>
                  )}
                  <button
                    className="btn-ghost"
                    onClick={onClickChangePayment}
                    disabled={!selectedShop?._id && !currentShop?.billing?.customerId}
                  >
                    支払い方法の変更
                  </button>

                  <button className="btn-primary" onClick={onSaveAll}>保存</button>
                </div>
              </div>
            </>
          ) : (
            <div style={{ color:'#aeb6c3' }}>
              {isMobile ? '上の「店舗を選択」から選んでください。' : '左の一覧から店舗を選択してください。'}
            </div>
          )}
        </div>
          </div>
        </div>
      </div>

      {msg && (
        <div className="fixed left-4 bottom-4 bg-card border border-border text-foreground px-4 py-3 rounded-lg shadow-lg z-50">
          {msg}
        </div>
      )}

      {/* 認証モーダル */}
      {showAuthModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">
              {pendingAction === 'save' ? '店舗情報の保存' : '店舗の削除'}
            </h3>
            <p className="text-gray-600 mb-4">
              認証のためパスワードを入力してください。
            </p>
            <input
              type="password"
              value={authPassword}
              onChange={(e) => setAuthPassword(e.target.value)}
              placeholder="パスワード"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600 mb-4"
              onKeyPress={(e) => {
                if (e.key === 'Enter' && authPassword && !modalError) {
                  if (pendingAction === 'save') {
                    executeSave();
                  } else if (pendingAction === 'delete') {
                    executeDelete();
                  }
                }
              }}
            />
            {modalError && (
              <div className="mb-4 text-red-600 text-sm" style={{ color: '#dc2626', fontSize: '14px' }}>
                {modalError}
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => {
                  setShowAuthModal(false);
                  setAuthPassword('');
                  setPendingAction(null);
                  setModalError('');
                }}
                className="btn-secondary"
              >
                キャンセル
              </button>
              <button
                onClick={() => {
                  if (pendingAction === 'save') {
                    executeSave();
                  } else if (pendingAction === 'delete') {
                    executeDelete();
                  }
                }}
                disabled={!authPassword}
                className="btn-primary"
                style={ !authPassword ? { opacity: 0.6, cursor: 'not-allowed' } : undefined }
              >
                決定
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
